import { useTranslate } from "@tolgee/react";
import { CheckCircle2, Copy, ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getArbiscanTxUrl } from "#/lib/explorer";
import { formatPlainNumber, formatSize } from "#/lib/formatters";

/** Shorten an address or tx hash to `0xAbCd...1234` */
export function truncateAddress(address: string) {
	return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/** Sanitize a decimal amount input string */
export function sanitizeAmountInput(value: string, maxDecimals = 6): string {
	const pattern = new RegExp(`^(\\d*\\.?\\d{0,${maxDecimals}}).*$`);
	return value.replace(/[^0-9.]/g, "").replace(pattern, "$1");
}

/** Format a numeric balance as a max amount string (USDC has 6 decimals). */
export function formatMaxAmount(value: number): string {
	return formatPlainNumber(value, 6);
}

/**
 * Prefill an amount input with its max value once, when the step first
 * becomes editable (`enabled`) and the max has loaded. Never overwrites an
 * existing value — user input or a carried-over amount is preserved. The
 * guard ref resets on unmount (dialog close), so reopening the dialog
 * re-fills with the latest balance.
 */
export function usePrefillMaxOnce(
	enabled: boolean,
	maxAmount: string,
	current: string,
	setAmount: (value: string) => void,
) {
	const done = useRef(false);
	useEffect(() => {
		if (done.current || !enabled || !maxAmount) return;
		done.current = true;
		if (!current) setAmount(maxAmount);
	}, [enabled, maxAmount, current, setAmount]);
}

/** Small pill showing a token icon + label */
export function TokenChip({
	iconSrc,
	label,
}: {
	iconSrc: string;
	label: string;
}) {
	return (
		<span className="inline-flex items-center gap-1.5 rounded-full bg-trade-surface-inset px-2 py-1 text-caption1-medium text-trade-text-secondary">
			<img src={iconSrc} width={14} height={14} alt={label} />
			{label}
		</span>
	);
}

/** "Available balance" card — USDC icon + label + amount. Shared by the
 *  withdraw flow's Step 1 (trading withdrawable) and Step 2 (wallet balance). */
export function AvailableUsdcCard({
	label,
	amount,
}: {
	label: string;
	amount: number;
}) {
	return (
		<div className="overflow-hidden rounded-[14px] border border-trade-border-subtle bg-trade-surface-inset">
			<div className="flex items-center gap-3 px-4 py-3">
				<img
					src="https://sdk-cdn.fun.xyz/images/usdc.svg"
					width={24}
					height={24}
					alt="USDC"
				/>
				<div className="min-w-0 flex-1">
					<p className="text-body2-bold text-trade-text-strong">USDC</p>
					<p className="text-label2 text-trade-text-secondary">{label}</p>
				</div>
				<div className="text-right">
					<p className="text-label1-tabular-bold text-trade-text-strong">
						{formatSize(amount, 6)} USDC
					</p>
				</div>
			</div>
		</div>
	);
}

/** Preview chip shown when a completion step is collapsed */
export function CompletionPreview({
	successMessage,
	txHash,
}: {
	successMessage: string;
	txHash: string | null;
}) {
	return (
		<div className="flex items-center gap-2">
			<CheckCircle2 className="h-3.5 w-3.5 text-primary" />
			<span>{txHash ? truncateAddress(txHash) : successMessage}</span>
		</div>
	);
}

/**
 * Full-screen terminal state with tx details and close button. `pending` swaps
 * the checkmark for a spinner — used while a deposit is still crediting to the
 * trading account (funds accepted on-chain but not yet landed). Defaults to the
 * completed (checkmark) look, so existing callers are unaffected.
 */
export function StepDone({
	onClose,
	successMessage,
	titleKey,
	txHash,
	pending = false,
}: {
	onClose: () => void;
	successMessage: string;
	titleKey: string;
	txHash: string | null;
	pending?: boolean;
}) {
	const { t } = useTranslate();
	const [copied, setCopied] = useState(false);

	function handleCopyTx() {
		if (!txHash) return;
		navigator.clipboard.writeText(txHash);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
		toast.success(t("actions.copied"));
	}

	return (
		<div className="flex flex-col items-center gap-4 py-2 text-center">
			<div className="flex h-16 w-16 items-center justify-center rounded-full border border-primary/35 bg-primary/10">
				{pending ? (
					<Loader2 className="h-8 w-8 animate-spin text-primary" />
				) : (
					<CheckCircle2 className="h-8 w-8 text-primary" />
				)}
			</div>

			<div className="max-w-[280px]">
				<p className="text-title1 text-trade-text-strong">
					{t(titleKey)}
				</p>
				<p className="mt-2 text-label1 text-trade-text-secondary">
					{successMessage}
				</p>
			</div>

			<div className="w-full rounded-[16px] border border-trade-border-subtle bg-trade-surface-inset px-4 py-3 text-left">
				<div className="flex items-center justify-between gap-3 text-label2 text-trade-text-secondary">
					<span>Network</span>
					<span className="text-label2-medium text-trade-text-strong">
						Arbitrum One
					</span>
				</div>
				{txHash && (
					<div className="mt-3 border-t border-trade-border-subtle pt-3">
						<div className="flex items-center justify-between gap-3 text-label2 text-trade-text-secondary">
							<span>{t("deposit.transaction-id")}</span>
							<span className="text-label2-medium text-trade-text-strong">
								{truncateAddress(txHash)}
							</span>
						</div>
						<div className="mt-3 flex gap-2">
							<button
								type="button"
								onClick={handleCopyTx}
								className="inline-flex min-h-9 flex-1 items-center justify-center gap-2 rounded-[12px] border border-trade-border-subtle bg-trade-surface px-3 text-label2-bold text-trade-text-strong transition-colors hover:bg-trade-interactive"
							>
								<Copy className="h-3.5 w-3.5" />
								{copied ? t("actions.copied") : t("deposit.copy-tx")}
							</button>
							<a
								href={getArbiscanTxUrl(txHash)}
								target="_blank"
								rel="noreferrer"
								className="inline-flex min-h-9 flex-1 items-center justify-center gap-2 rounded-[12px] border border-primary/20 bg-primary/10 px-3 text-label2-bold text-primary transition-colors hover:bg-primary/15"
							>
								<ExternalLink className="h-3.5 w-3.5" />
								{t("deposit.view-transaction")}
							</a>
						</div>
					</div>
				)}
			</div>

			<button
				type="button"
				onClick={onClose}
				className="mt-2 flex min-h-12 w-full items-center justify-center gap-2 rounded-[14px] bg-primary text-body2-bold text-primary-foreground transition-[filter,transform,opacity] hover:brightness-105 cursor-pointer"
			>
				{t("actions.done")}
			</button>
		</div>
	);
}
